import { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";

export interface NotificationItem {
	id: number;
	type: string;
	severity: "info" | "success" | "warning" | "error";
	title: string;
	body: string;
	href: string | null;
	metadata: Record<string, any> | null;
	read_at: string | null;
	created_at: string;
}

const API_BASE = process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN || "";
const POLL_INTERVAL_MS = 60_000;

/**
 * Fetches notifications + unread count for the active company.
 * Polls every minute while the tab is visible.
 */
export function useNotifications() {
	const { getAccessTokenSilently, isAuthenticated } = useAuth0();
	const [items, setItems] = useState<NotificationItem[]>([]);
	const [unreadCount, setUnreadCount] = useState(0);
	const [loading, setLoading] = useState(false);

	const authFetch = useCallback(
		async (path: string, init?: RequestInit) => {
			const token = await getAccessTokenSilently();
			const res = await fetch(`${API_BASE}${path}`, {
				...init,
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
					...(init?.headers || {}),
				},
			});
			if (!res.ok) {
				throw new Error(`Notifications ${path} -> HTTP ${res.status}`);
			}
			return res.json();
		},
		[getAccessTokenSilently],
	);

	const refresh = useCallback(async () => {
		if (!isAuthenticated) return;
		setLoading(true);
		try {
			const [list, count] = await Promise.all([
				authFetch("/api/notifications?limit=20"),
				authFetch("/api/notifications/unread-count"),
			]);
			setItems(list?.Data?.notifications || []);
			setUnreadCount(count?.Data?.count || 0);
		} catch (err) {
			// Soft-fail — bell stays in last known state, don't crash the app
			console.error("[notifications] refresh failed:", err);
		} finally {
			setLoading(false);
		}
	}, [authFetch, isAuthenticated]);

	const markRead = useCallback(
		async (id: number) => {
			// Optimistic update first
			setItems((prev) =>
				prev.map((n) =>
					n.id === id
						? { ...n, read_at: new Date().toISOString() }
						: n,
				),
			);
			setUnreadCount((c) => Math.max(0, c - 1));
			try {
				await authFetch(`/api/notifications/${id}/read`, {
					method: "PATCH",
				});
			} catch (err) {
				console.error("[notifications] markRead failed:", err);
				refresh();
			}
		},
		[authFetch, refresh],
	);

	const markAllRead = useCallback(async () => {
		setItems((prev) =>
			prev.map((n) =>
				n.read_at ? n : { ...n, read_at: new Date().toISOString() },
			),
		);
		setUnreadCount(0);
		try {
			await authFetch(`/api/notifications/read-all`, { method: "PATCH" });
		} catch (err) {
			console.error("[notifications] markAllRead failed:", err);
			refresh();
		}
	}, [authFetch, refresh]);

	const dismiss = useCallback(
		async (id: number) => {
			const wasUnread = items.find((n) => n.id === id)?.read_at == null;
			setItems((prev) => prev.filter((n) => n.id !== id));
			if (wasUnread) setUnreadCount((c) => Math.max(0, c - 1));
			try {
				await authFetch(`/api/notifications/${id}`, {
					method: "DELETE",
				});
			} catch (err) {
				console.error("[notifications] dismiss failed:", err);
				refresh();
			}
		},
		[authFetch, items, refresh],
	);

	// Initial load + poll while authenticated and tab visible.
	useEffect(() => {
		if (!isAuthenticated) return;
		refresh();
		const tick = () => {
			if (document.visibilityState === "visible") refresh();
		};
		const id = window.setInterval(tick, POLL_INTERVAL_MS);
		document.addEventListener("visibilitychange", tick);
		return () => {
			window.clearInterval(id);
			document.removeEventListener("visibilitychange", tick);
		};
	}, [isAuthenticated, refresh]);

	return {
		items,
		unreadCount,
		loading,
		refresh,
		markRead,
		markAllRead,
		dismiss,
	};
}
